import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import Logo from '@/components/Logo';
import { useAuth } from '@/context/AuthContext';
import {
  MapPin,
  Phone,
  Clock,
  Star,
  Calendar,
  Building2,
  ChevronLeft,
  Loader2,
  CheckCircle,
  Globe,
  Mail,
  AlertCircle,
  Ambulance,
  Activity,
  Package,
  ShoppingCart,
  MessageCircle,
  Info,
  UserPlus,
  LogIn
} from 'lucide-react';
import { toast } from 'sonner';

const FACILITY_TYPE_LABELS = {
  hospital: "Hôpital",
  clinic: "Clinique",
  medical_center: "Centre médical",
  laboratory: "Laboratoire",
  pharmacy: "Pharmacie",
  imaging_center: "Centre d'imagerie",
  dental_clinic: "Cabinet dentaire",
  maternity: "Maternité"
};

const DAY_LABELS = {
  monday: "Lundi",
  tuesday: "Mardi",
  wednesday: "Mercredi",
  thursday: "Jeudi",
  friday: "Vendredi",
  saturday: "Samedi",
  sunday: "Dimanche"
};

const PublicFacilityProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  
  // Products state
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Order modal state
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderForm, setOrderForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    quantity: 1,
    notes: ''
  });
  const [submittingOrder, setSubmittingOrder] = useState(false);
  
  // Login prompt modal
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [slug]);

  const loadProfile = async () => {
    try {
      const response = await api.get(`/seo/etablissement/${slug}`);
      setData(response.data);
      
      // Load products for this facility
      if (response.data?.facility?.id) {
        loadProducts(response.data.facility.id);
      }
    } catch (err) {
      console.error('Error loading facility:', err);
      setError('Établissement non trouvé');
    } finally {
      setLoading(false);
    }
  };
  
  const loadProducts = async (facilityId) => {
    setLoadingProducts(true);
    try {
      const response = await api.get(`/products/facility/${facilityId}/public`);
      setProducts(response.data.products || []);
      setProductCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };
  
  const openOrderModal = (product) => {
    setSelectedProduct(product);
    setOrderForm({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      quantity: 1,
      notes: ''
    });
    setShowOrderModal(true);
  };
  
  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (!orderForm.customer_name || !orderForm.customer_phone) {
      toast.error('Veuillez remplir votre nom et téléphone');
      return;
    }
    
    setSubmittingOrder(true);
    try {
      await api.post('/products/orders/public', {
        product_id: selectedProduct.id,
        customer_name: orderForm.customer_name,
        customer_phone: orderForm.customer_phone,
        customer_email: orderForm.customer_email || null,
        quantity: orderForm.quantity,
        notes: orderForm.notes || null
      });
      
      toast.success('Commande envoyée ! L\'établissement vous contactera bientôt.');
      setShowOrderModal(false);
      setSelectedProduct(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la commande');
    } finally {
      setSubmittingOrder(false);
    }
  };
  
  const handleWhatsAppContact = (product) => {
    const phone = data?.facility?.whatsapp || data?.facility?.phone;
    if (!phone) {
      toast.error('Numéro WhatsApp non disponible');
      return;
    }
    const cleanPhone = phone.replace(/\D/g, '');
    const message = encodeURIComponent(
      `Bonjour, je suis intéressé(e) par "${product.name}" (${product.price?.toLocaleString()} FCFA) vu sur DOCKHTA. Pouvez-vous me donner plus d'informations ?`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };
  
  const handleBookAppointment = () => {
    if (isAuthenticated) {
      // User is logged in, redirect to search with facility filter
      navigate(`/search?facility=${facility?.id}`);
    } else {
      // User not logged in, show login prompt modal
      setShowLoginPrompt(true);
    }
  };
  
  const filteredProducts = selectedCategory
    ? products.filter(p => p.category === selectedCategory)
    : products;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1976D2]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" data-testid="logo-link">
              <Logo size="sm" showSlogan={false} />
            </Link>
            <Button variant="outline" onClick={() => navigate(-1)} size="sm">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>
          </div>
        </header>
        
        <div className="flex items-center justify-center p-4 mt-20">
          <Card className="max-w-md w-full">
            <CardContent className="p-8 text-center">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-slate-900 mb-2">Établissement non trouvé</h1>
              <p className="text-slate-500 mb-6">Cet établissement n'existe pas ou n'est plus disponible.</p>
              <div className="flex flex-col gap-3">
                <Button onClick={() => navigate('/etablissements')} className="bg-[#1976D2]">
                  Voir tous les établissements
                </Button>
                <Button variant="outline" onClick={() => navigate('/rechercher')}>
                  Rechercher un médecin
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { facility, professionals_count, reviews, stats, seo } = data;

  // Structured data for Google (JSON-LD)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": facility?.name,
    "description": facility?.description,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": facility?.address,
      "addressLocality": facility?.city,
      "addressCountry": "CM"
    },
    "telephone": facility?.phone,
    "url": facility?.website,
    "aggregateRating": stats?.reviews_count > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": stats?.average_rating,
      "reviewCount": stats?.reviews_count
    } : undefined,
    "openingHoursSpecification": facility?.opening_hours ? 
      Object.entries(facility.opening_hours).filter(([day, hours]) => DAY_LABELS[day] && !hours?.closed).map(([day, hours]) => ({
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": day.charAt(0).toUpperCase() + day.slice(1),
        "opens": hours?.open,
        "closes": hours?.close
      })) : undefined
  };

  return (
    <>
      <Helmet>
        <title>{seo?.title}</title>
        <meta name="description" content={seo?.description} />
        <link rel="canonical" href={`https://dockhta.com${seo?.canonical_url}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seo?.title} />
        <meta property="og:description" content={seo?.description} />
        <meta property="og:type" content="business.business" />
        <meta property="og:url" content={`https://dockhta.com${seo?.canonical_url}`} />
        <meta property="og:image" content={facility?.cover_image || 'https://dockhta.com/og-image.png'} />
        <meta property="og:site_name" content="DOCKHTA" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo?.title} />
        <meta name="twitter:description" content={seo?.description} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" data-testid="logo-link">
              <Logo size="sm" showSlogan={false} />
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/etablissements">
                <Button variant="ghost" size="sm" className="text-slate-600 hidden sm:flex">
                  Établissements
                </Button>
              </Link>
              <Button variant="outline" onClick={() => navigate(-1)} size="sm">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Retour
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto p-4 pb-24">
          {/* Facility Header Card */}
          <Card className="mb-6 border-0 shadow-lg overflow-hidden">
            {/* Cover Image */}
            {facility?.cover_image && (
              <div className="h-48 bg-gradient-to-r from-indigo-500 to-purple-600">
                <img src={facility.cover_image} alt={facility.name} className="w-full h-full object-cover" />
              </div>
            )}
            
            <div className={`${!facility?.cover_image ? 'bg-gradient-to-r from-indigo-600 to-purple-600' : ''} p-6 text-white ${facility?.cover_image ? 'bg-gradient-to-t from-black/70 to-transparent -mt-24 relative' : ''}`}>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Building2 className="w-8 h-8 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold">{facility?.name}</h1>
                  <p className="text-white/90">{FACILITY_TYPE_LABELS[facility?.facility_type] || facility?.facility_type}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {facility?.is_verified && (
                      <Badge className="bg-white/20 text-white">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Vérifié
                      </Badge>
                    )}
                    {facility?.emergency_24h && (
                      <Badge className="bg-red-500/80 text-white">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Urgences 24h
                      </Badge>
                    )}
                    {facility?.has_ambulance && (
                      <Badge className="bg-blue-500/80 text-white">
                        <Ambulance className="w-3 h-3 mr-1" />
                        Ambulance
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <CardContent className="p-5">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-5 text-center">
                <div className="bg-amber-50 rounded-xl p-3">
                  <div className="flex items-center justify-center gap-1 text-amber-600">
                    <Star className="w-5 h-5 fill-amber-400" />
                    <span className="font-bold text-xl">{stats?.average_rating || '-'}</span>
                  </div>
                  <p className="text-xs text-slate-500">{stats?.reviews_count || 0} avis</p>
                </div>
                <div className="bg-indigo-50 rounded-xl p-3">
                  <p className="font-bold text-xl text-indigo-600">{professionals_count || 0}</p>
                  <p className="text-xs text-slate-500">Praticiens</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3">
                  <p className="font-bold text-xl text-green-600">{products.length}</p>
                  <p className="text-xs text-slate-500">Produits/Services</p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                {facility?.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{facility.address}</p>
                      <p className="text-sm text-slate-500">{facility.city}</p>
                    </div>
                  </div>
                )}
                {facility?.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <a href={`tel:${facility.phone}`} className="font-medium text-[#1976D2]">{facility.phone}</a>
                  </div>
                )}
                {facility?.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <a href={`mailto:${facility.email}`} className="font-medium text-[#1976D2]">{facility.email}</a>
                  </div>
                )}
                {facility?.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-slate-400" />
                    <a href={facility.website.startsWith('http') ? facility.website : `https://${facility.website}`} target="_blank" rel="noopener noreferrer" className="font-medium text-[#1976D2]">{facility.website}</a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2 h-12">
              <TabsTrigger value="info" className="flex items-center gap-2 text-sm">
                <Info className="w-4 h-4" />
                Informations
              </TabsTrigger>
              <TabsTrigger value="catalog" className="flex items-center gap-2 text-sm">
                <Package className="w-4 h-4" />
                Catalogue ({products.length})
              </TabsTrigger>
            </TabsList>

            {/* Info Tab */}
            <TabsContent value="info" className="space-y-4">
              {/* Description */}
              {facility?.description && (
                <Card className="border-0 shadow-md">
                  <CardContent className="p-5">
                    <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-indigo-600" />
                      À propos
                    </h2>
                    <p className="text-slate-600 leading-relaxed">{facility.description}</p>
                  </CardContent>
                </Card>
              )}

              {/* Specialties & Services */}
              {(facility?.specialties?.length > 0 || facility?.services?.length > 0) && (
                <Card className="border-0 shadow-md">
                  <CardContent className="p-5">
                    <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Activity className="w-5 h-5 text-green-600" />
                      Services proposés
                    </h2>
                    
                    {facility?.specialties?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-500 mb-2">Spécialités</p>
                        <div className="flex flex-wrap gap-2">
                          {facility.specialties.map((spec, i) => (
                            <Badge key={i} className="bg-indigo-100 text-indigo-700 border-0">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {facility?.services?.length > 0 && (
                      <div>
                        <p className="text-sm text-slate-500 mb-2">Services</p>
                        <div className="flex flex-wrap gap-2">
                          {facility.services.map((service, i) => (
                            <Badge key={i} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              {service}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Opening Hours */}
              {facility?.opening_hours && !facility?.always_open && typeof facility.opening_hours === 'object' && (
                <Card className="border-0 shadow-md">
                  <CardContent className="p-5">
                    <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-600" />
                      Horaires d'ouverture
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {Object.entries(facility.opening_hours)
                        .filter(([day]) => DAY_LABELS[day])
                        .map(([day, hours]) => (
                        <div key={day} className="flex justify-between items-center bg-slate-50 rounded-lg px-4 py-2">
                          <span className="font-medium text-slate-700">{DAY_LABELS[day]}</span>
                          <span className={hours?.closed ? 'text-red-500' : 'text-green-600 font-medium'}>
                            {hours?.closed ? 'Fermé' : `${hours?.open || '?'} - ${hours?.close || '?'}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {facility?.always_open && (
                <Card className="border-0 shadow-md bg-green-50">
                  <CardContent className="p-5 flex items-center gap-3">
                    <Clock className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">Ouvert 24h/24, 7j/7</p>
                      <p className="text-sm text-green-600">Cet établissement est disponible à tout moment</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Insurance */}
              {facility?.insurance_accepted?.length > 0 && (
                <Card className="border-0 shadow-md">
                  <CardContent className="p-5">
                    <h2 className="font-semibold text-slate-900 mb-3">Assurances acceptées</h2>
                    <div className="flex flex-wrap gap-2">
                      {facility.insurance_accepted.map((ins, i) => (
                        <Badge key={i} variant="outline">{ins}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Reviews */}
              {reviews?.length > 0 && (
                <Card className="border-0 shadow-md">
                  <CardContent className="p-5">
                    <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-500" />
                      Avis ({reviews.length})
                    </h2>
                    <div className="space-y-4">
                      {reviews.slice(0, 5).map((review, i) => (
                        <div key={i} className="border-b last:border-0 pb-4 last:pb-0">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-4 h-4 ${star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                                />
                              ))}
                            </div>
                            <span className="text-sm text-slate-500">
                              {new Date(review.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          {review.comment && <p className="text-slate-600 text-sm">{review.comment}</p>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Catalog Tab */}
            <TabsContent value="catalog" className="space-y-4">
              {loadingProducts ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : products.length === 0 ? (
                <Card className="border-0 shadow-md">
                  <CardContent className="py-12 text-center">
                    <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-600">Aucun produit disponible</h3>
                    <p className="text-slate-500 text-sm mt-1">
                      Cet établissement n'a pas encore ajouté de produits ou services.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Category Filter */}
                  {productCategories.length > 1 && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant={selectedCategory === '' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedCategory('')}
                        className={selectedCategory === '' ? 'bg-indigo-600' : ''}
                      >
                        Tout ({products.length})
                      </Button>
                      {productCategories.map((cat) => (
                        <Button
                          key={cat}
                          variant={selectedCategory === cat ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedCategory(cat)}
                          className={selectedCategory === cat ? 'bg-indigo-600' : ''}
                        >
                          {cat} ({products.filter(p => p.category === cat).length})
                        </Button>
                      ))}
                    </div>
                  )}

                  {/* Products Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {filteredProducts.map((product) => (
                      <Card key={product.id} className="border-0 shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                        {product.image_url && (
                          <div className="h-40 bg-slate-100">
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-slate-900 line-clamp-1">{product.name}</h3>
                              <Badge variant="outline" className="text-xs mt-1">
                                {product.category}
                              </Badge>
                            </div>
                          </div>
                          
                          {product.description && (
                            <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                              {product.description}
                            </p>
                          )}
                          
                          <div className="flex items-center justify-between pt-3 border-t">
                            <p className="text-lg font-bold text-[#E53935]">
                              {product.price?.toLocaleString()} FCFA
                            </p>
                            <div className="flex gap-2">
                              {(facility?.whatsapp || facility?.phone) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleWhatsAppContact(product)}
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  title="Contacter via WhatsApp"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() => openOrderModal(product)}
                                className="bg-[#E53935] hover:bg-[#C62828]"
                              >
                                <ShoppingCart className="w-4 h-4 mr-1" />
                                Commander
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>

          {/* CTA */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-lg">
            <div className="max-w-4xl mx-auto flex gap-3">
              {facility?.phone && (
                <Button
                  variant="outline"
                  onClick={() => window.location.href = `tel:${facility.phone}`}
                  className="flex-1 h-12 rounded-xl"
                  data-testid="call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Appeler
                </Button>
              )}
              <Button
                onClick={handleBookAppointment}
                className="flex-1 bg-[#E53935] hover:bg-[#C62828] h-12 rounded-xl text-base font-medium"
                data-testid="book-appointment-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Prendre rendez-vous
              </Button>
            </div>
          </div>
        </main>
      </div>

      {/* Login Prompt Modal */}
      <Dialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-[#E53935]/10 rounded-full flex items-center justify-center mb-4">
              <Calendar className="w-8 h-8 text-[#E53935]" />
            </div>
            <DialogTitle className="text-center text-xl">Connectez-vous pour réserver</DialogTitle>
            <DialogDescription className="text-center">
              Pour prendre rendez-vous chez <span className="font-semibold">{facility?.name}</span>, vous devez être connecté à votre compte DOCKHTA.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-4">
            <Button 
              onClick={() => navigate('/login', { state: { from: `/etablissement/${slug}` } })}
              className="w-full h-12 bg-[#E53935] hover:bg-[#C62828]"
            >
              <LogIn className="w-5 h-5 mr-2" />
              Se connecter
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/register', { state: { from: `/etablissement/${slug}` } })}
              className="w-full h-12"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Créer un compte gratuit
            </Button>
            <p className="text-center text-xs text-slate-500 pt-2">
              L'inscription est gratuite et prend moins d'une minute
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Commander ce produit</DialogTitle>
            <DialogDescription>
              Remplissez vos informations pour passer commande. L'établissement vous contactera pour confirmer.
            </DialogDescription>
          </DialogHeader>

          {selectedProduct && (
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                {selectedProduct.image_url ? (
                  <img 
                    src={selectedProduct.image_url} 
                    alt={selectedProduct.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                    <Package className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900">{selectedProduct.name}</h4>
                  <p className="text-lg font-bold text-[#E53935]">
                    {selectedProduct.price?.toLocaleString()} FCFA
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmitOrder} className="space-y-4">
            <div>
              <Label>Votre nom complet *</Label>
              <Input
                value={orderForm.customer_name}
                onChange={(e) => setOrderForm({ ...orderForm, customer_name: e.target.value })}
                placeholder="Ex: Jean Dupont"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Numéro de téléphone *</Label>
              <Input
                value={orderForm.customer_phone}
                onChange={(e) => setOrderForm({ ...orderForm, customer_phone: e.target.value })}
                placeholder="Ex: 6XX XXX XXX"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Email (optionnel)</Label>
              <Input
                type="email"
                value={orderForm.customer_email}
                onChange={(e) => setOrderForm({ ...orderForm, customer_email: e.target.value })}
                placeholder="Ex: jean@email.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Quantité</Label>
              <Input
                type="number"
                min="1"
                value={orderForm.quantity}
                onChange={(e) => setOrderForm({ ...orderForm, quantity: parseInt(e.target.value) || 1 })}
                className="mt-1 w-24"
              />
            </div>

            <div>
              <Label>Message / Notes (optionnel)</Label>
              <Textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                placeholder="Informations supplémentaires..."
                className="mt-1"
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowOrderModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={submittingOrder}
                className="flex-1 bg-[#E53935] hover:bg-[#C62828]"
              >
                {submittingOrder ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Envoyer la commande
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PublicFacilityProfile;
